// src/utils/speech.js
// Aussprache: zuerst vorab vertonte Audiodateien (Google Cloud TTS, erzeugt mit
// scripts/tts/build-audio.js, liegen öffentlich in Cloudflare R2), sonst die eingebaute
// Sprachausgabe des Browsers (Web Speech API, Qualität je nach Gerät).

import { audioKey, textForSpeech } from "./speechText";

export { textForSpeech };

// Muss zu VOICE_SET.id in scripts/tts/build-audio.js passen.
const AUDIO_VOICE_SET = "google-chirp3hd-v1";
// Sprachen, für die es Audiodateien gibt.
const AUDIO_LANGS = new Set(["es", "fr"]);
// Öffentliche Adresse des R2-Buckets (nicht geheim). Vorerst die r2.dev-Adresse, später
// audio.fluent-app.de; REACT_APP_AUDIO_BASE_URL überschreibt sie, leer = nur Browser-Stimme.
const AUDIO_BASE =
  process.env.REACT_APP_AUDIO_BASE_URL ?? "https://pub-18ed6e74ed484c46b56731ef585fb3ea.r2.dev";

// App-Sprachcode -> Sprache/Region der Stimme
const SPEECH_LOCALES = {
  es: "es-ES",
  de: "de-DE",
  en: "en-US",
  fr: "fr-FR",
  it: "it-IT",
  nl: "nl-NL",
};

export function isSpeechSupported() {
  return typeof window !== "undefined" && ("speechSynthesis" in window || "Audio" in window);
}

// Bevorzugt eine Stimme der genauen Region (es-ES), sonst irgendeine der Sprache (es-*).
function pickVoice(locale) {
  const voices = window.speechSynthesis.getVoices();
  const lang = locale.split("-")[0];
  return (
    voices.find((v) => v.lang.replace("_", "-") === locale) ||
    voices.find((v) => v.lang.toLowerCase().startsWith(lang)) ||
    null
  );
}

function speakWithBrowser(cleaned, langCode) {
  if (!("speechSynthesis" in window)) return;
  const locale = SPEECH_LOCALES[langCode] || langCode;
  const utterance = new SpeechSynthesisUtterance(cleaned);
  utterance.lang = locale;
  utterance.rate = 0.9; // etwas langsamer als normal, besser zum Mitsprechen
  const voice = pickVoice(locale);
  if (voice) utterance.voice = voice;
  window.speechSynthesis.speak(utterance);
}

let currentAudio = null;
let speakCount = 0;
// Texte ohne Audiodatei (oder mit Ladefehler) gehen gleich an die Browser-Stimme.
const missingAudio = new Set();

function stopSpeaking() {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio = null;
  }
  if ("speechSynthesis" in window) window.speechSynthesis.cancel();
}

export function speak(text, langCode) {
  if (!isSpeechSupported()) return;
  const cleaned = textForSpeech(text);
  if (!cleaned) return;

  // Laufende Ausgabe abbrechen, damit schnelles Mehrfachtippen nicht stapelt.
  stopSpeaking();
  const call = ++speakCount;

  const key = audioKey(cleaned);
  if (!AUDIO_BASE || !AUDIO_LANGS.has(langCode) || !("Audio" in window) || missingAudio.has(key)) {
    speakWithBrowser(cleaned, langCode);
    return;
  }

  // play() direkt im Klick aufrufen (iOS), Fehler (z. B. Datei fehlt) → Browser-Stimme.
  const audio = new Audio(`${AUDIO_BASE}/${langCode}/${AUDIO_VOICE_SET}/${key}.mp3`);
  currentAudio = audio;
  audio.play().catch((err) => {
    if (err?.name === "AbortError" || call !== speakCount) return; // durch neues Vorlesen abgelöst
    missingAudio.add(key);
    currentAudio = null;
    speakWithBrowser(cleaned, langCode);
  });
}
